/**
 * Cliente HTTP do painel.
 *
 * O contrato vem das rotas de `apps/api/src/rotas`. Nada aqui inventa campo:
 * se o tipo divergir, o painel quebra no typecheck — não em produção.
 */

export interface Branding {
  nomeExibicao: string;
  corPrimaria: string;
  corAcento: string;
  logoUrl: string | null;
  suporte: string;
}

export type StatusDisparo =
  | 'pendente'
  | 'na_fila'
  | 'enviado'
  | 'falhou'
  | 'suprimido'
  | 'cancelado';

export type IdReceita =
  | 'lembrete_preventivo'
  | 'dia_vencimento'
  | 'inadimplencia_leve'
  | 'inadimplencia_critica'
  | 'aniversario';

export interface Janela {
  inicio: string;
  fim: string;
}

export interface MarcaPublica {
  slug: string;
  nome: string;
  branding: Branding;
  fuso: string;
  janela: Janela;
}

export interface UsuarioSessao {
  usuarioId: string;
  tenantId: string;
  nome: string;
  email: string;
  papel: 'admin' | 'atendente';
}

export interface ProvedorSessao {
  id: string;
  slug: string;
  nome: string;
  branding: Branding;
  fuso: string;
  janela: Janela;
}

export interface ProvedorProvisionamento {
  id: string;
  slug: string;
  nome: string;
  ativo: boolean;
  criadoEm: string;
}

export interface EntradaProvisionamento {
  slug: string;
  nome: string;
  fuso: string;
  janelaInicio: string;
  janelaFim: string;
  adminNome: string;
  adminEmail: string;
  senhaInicial: string;
}

export interface RespostaSessao {
  usuario: UsuarioSessao;
  provedor: ProvedorSessao | null;
}

export interface Disparo {
  id: string;
  chave: string;
  receita: IdReceita;
  canalId: string | null;
  templateId: string | null;
  documento: string;
  nome: string;
  telefone: string | null;
  contrato: string;
  fatura: string | null;
  vencimento: string | null;
  valorCentavos: number | null;
  variaveis: Record<string, string>;
  acaoErp: { tipo: 'desbloqueio'; dias: number } | null;
  status: StatusDisparo;
  agendadoPara: string;
  proximaTentativaEm: string;
  tentativas: number;
  ultimoErro: string | null;
  motivoSupressao: string | null;
  recibo: string | null;
  situacaoCanal: string | null;
  enviadoEm: string | null;
  criadoEm: string;
  atualizadoEm: string;
}

export interface Resumo {
  provedor: {
    nome: string;
    fuso: string;
    janela: Janela;
    branding: Branding;
  } | null;
  disparos: {
    hoje: Partial<Record<StatusDisparo, number>>;
    pendentes: number;
    falhasAbertas: number;
    proximos: Disparo[];
  };
  eventos: { faturas: number; titulares: number };
  ultimaImportacao: Importacao | null;
  integracoes: {
    elo: { modo: 'real' | 'demo' | 'nao_configurado'; baseUrl: string | null; ultimoTesteOk: boolean | null };
    canal: { nome: string; fila: number; vazaoPorMinuto: number; ativo: boolean } | null;
  };
  receitasAtivas: number;
  receitasTotal: number;
}

export interface Importacao {
  id: string;
  arquivo: string;
  faturas: number;
  titulares: number;
  ignorados: number;
  criadoEm: string;
}

export interface RespostaImportacao {
  faturas: number;
  titulares: number;
  ignorados: number;
  erros: string[];
}

export interface Fatura {
  id: string;
  origemId: string;
  documento: string;
  nome: string;
  telefone: string | null;
  contrato: string;
  fatura: string;
  vencimento: string;
  valorCentavos: number;
  situacao: 'aberta' | 'paga';
  importadoEm: string;
}

export interface Titular {
  id: string;
  origemId: string;
  documento: string;
  nome: string;
  telefone: string | null;
  contrato: string;
  nascimento: string;
  importadoEm: string;
}

export interface Optout {
  tenantId: string;
  documento: string;
  motivo: string;
  criadoEm: string;
}

export interface ParametroReceita {
  chave: 'dias_antes' | 'dias_apos' | 'dias_desbloqueio' | 'hora';
  rotulo: string;
  ajuda: string;
  tipo: 'inteiro' | 'hora';
  minimo?: number;
  maximo?: number;
  padrao: number | string;
  sufixo?: string;
}

export interface Receita {
  id: IdReceita;
  rotulo: string;
  resumo: string;
  gatilho: string;
  acao: string;
  acaoErp: boolean;
  categoria: 'cobranca' | 'relacionamento';
  exigeTemplate: boolean;
  parametros: ParametroReceita[];
  variaveis: string[];
  corpoPadrao: string;
  ativa: boolean;
  valores: { hora: string; dias_antes?: number; dias_apos?: number; dias_desbloqueio?: number } | null;
  templateId: string | null;
  canalId: string | null;
  atualizadoEm: string | null;
}

export interface Template {
  id: string;
  chave: string;
  nome: string;
  canal: string;
  corpo: string;
  metaTemplateId: number | null;
  aprovado: boolean;
  criadoEm: string;
  atualizadoEm: string;
}

export interface Canal {
  id: string;
  nome: string;
  tipo: 'whatsapp';
  xmaxQueueId: number;
  numeroOrigem: string;
  vazaoPorMinuto: number;
  concorrencia: number;
  ativo: boolean;
  apiKeyInformada: boolean;
}

export interface Configuracoes {
  provedor: {
    nome: string;
    slug: string;
    fuso: string;
    janelaInicio: string;
    janelaFim: string;
    branding: Branding;
  } | null;
  canais: Canal[];
  elo: {
    baseUrl: string;
    modo: 'real' | 'demo';
    tokenInformado: boolean;
    ultimoTesteEm: string | null;
    ultimoTesteOk: boolean | null;
    ultimoTesteDetalhe: string | null;
  } | null;
  ambiente: { xmaxModo: 'real' | 'demo'; eloUrlPadrao: string };
}

export interface ResultadoPlanejamento {
  criados: number;
  ids: string[];
  liberadosAgora?: number;
  avisos: string[];
  fonteEventos: 'elo' | 'importacao';
  periodo: { hoje: string; fuso: string; janela: Janela };
  ignorados: Array<{ receita: string; origemId: string; motivo: string; detalhe: string }>;
  planejados: Array<{
    receita: IdReceita;
    nome: string;
    documento: string;
    agendadoPara: string;
    fatura: string | null;
  }>;
}

export interface EventoAuditoria {
  id: string;
  ator: string;
  acao: string;
  detalhe: Record<string, unknown>;
  criadoEm: string;
}

export class ErroApi extends Error {
  readonly status: number;
  readonly campos: Record<string, string> | undefined;

  constructor(mensagem: string, status: number, campos?: Record<string, string>) {
    super(mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.campos = campos;
  }
}

interface RespostaErro {
  erro?: string;
  campos?: Record<string, string>;
}

async function requisitar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  // O Fastify recusa corpo vazio com Content-Type: application/json. Só
  // anuncia JSON quando há corpo de verdade.
  const cabecalhos = new Headers(init.headers);
  if (init.body !== undefined) cabecalhos.set('Content-Type', 'application/json');

  let resposta: Response;
  try {
    resposta = await fetch(`/api${caminho}`, {
      credentials: 'same-origin',
      ...init,
      headers: cabecalhos,
    });
  } catch {
    throw new ErroApi(
      'Sem conexão com o motor. Verifique se a API está no ar.',
      0,
    );
  }

  const texto = await resposta.text();
  let corpo: unknown = null;
  if (texto !== '') {
    try {
      corpo = JSON.parse(texto) as unknown;
    } catch {
      corpo = null;
    }
  }

  if (!resposta.ok) {
    const detalhe = (corpo ?? {}) as RespostaErro;
    throw new ErroApi(
      detalhe.erro ?? `Falha inesperada (HTTP ${resposta.status}).`,
      resposta.status,
      detalhe.campos,
    );
  }

  return corpo as T;
}

const enviar = <T>(caminho: string, metodo: 'POST' | 'PUT' | 'DELETE', corpo?: unknown): Promise<T> =>
  requisitar<T>(caminho, {
    method: metodo,
    ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
  });

export const api = {
  marca: () => requisitar<MarcaPublica>('/publico/marca'),
  entrar: (email: string, senha: string) =>
    enviar<{ usuario: { nome: string; email: string; papel: string } }>('/auth/entrar', 'POST', {
      email,
      senha,
    }),
  sair: () => enviar<{ autenticado: boolean }>('/auth/sair', 'POST'),
  sessao: () => requisitar<RespostaSessao>('/auth/sessao'),

  resumo: () => requisitar<Resumo>('/resumo'),
  auditoria: () => requisitar<{ eventos: EventoAuditoria[] }>('/auditoria'),

  receitas: () => requisitar<{ receitas: Receita[] }>('/receitas'),
  salvarReceita: (
    tipo: IdReceita,
    dados: {
      ativa: boolean;
      parametros: Record<string, string | number>;
      templateId: string | null;
      canalId: string | null;
    },
  ) => enviar<{ ok: boolean; parametros: Record<string, unknown> }>(`/receitas/${tipo}`, 'PUT', dados),

  templates: () => requisitar<{ templates: Template[] }>('/templates'),
  criarTemplate: (dados: {
    chave: string;
    nome: string;
    corpo: string;
    metaTemplateId: number | null;
  }) => enviar<{ template: Template }>('/templates', 'POST', dados),
  atualizarTemplate: (
    id: string,
    dados: Partial<{ nome: string; corpo: string; metaTemplateId: number | null; aprovado: boolean }>,
  ) => enviar<{ ok: boolean }>(`/templates/${id}`, 'PUT', dados),
  removerTemplate: (id: string) => enviar<{ ok: boolean }>(`/templates/${id}`, 'DELETE'),

  disparos: (filtro: {
    status?: string;
    receita?: string;
    busca?: string;
    pagina?: number;
    porPagina?: number;
  }) => {
    const parametros = new URLSearchParams();
    for (const [chave, valor] of Object.entries(filtro)) {
      if (valor !== undefined && valor !== '') parametros.set(chave, String(valor));
    }
    const consulta = parametros.toString();
    return requisitar<{
      itens: Disparo[];
      total: number;
      pagina: number;
      porPagina: number;
    }>(`/disparos${consulta ? `?${consulta}` : ''}`);
  },
  planejar: () => enviar<ResultadoPlanejamento>('/disparos/planejar', 'POST'),
  simular: () => enviar<ResultadoPlanejamento>('/disparos/simular', 'POST'),
  reprocessarDisparo: (id: string) => enviar<{ ok: boolean }>(`/disparos/${id}/reprocessar`, 'POST'),
  cancelarDisparo: (id: string) => enviar<{ ok: boolean }>(`/disparos/${id}/cancelar`, 'POST'),

  eventos: () =>
    requisitar<{
      contagens: { faturas: number; titulares: number };
      faturas: Fatura[];
      titulares: Titular[];
      ultimaImportacao: Importacao | null;
      modelo: string;
    }>('/eventos'),
  importar: (arquivo: string, conteudo: string, formato: 'auto' | 'csv' | 'json' = 'auto') =>
    enviar<RespostaImportacao>('/eventos/importar', 'POST', { arquivo, conteudo, formato }),
  optouts: () => requisitar<{ optouts: Optout[] }>('/optouts'),
  adicionarOptout: (documento: string, motivo: string) =>
    enviar<{ ok: boolean }>('/optouts', 'POST', { documento, motivo }),
  removerOptout: (documento: string) =>
    enviar<{ ok: boolean }>(`/optouts/${encodeURIComponent(documento)}`, 'DELETE'),

  configuracoes: () => requisitar<Configuracoes>('/configuracoes'),
  listarProvedoresInterno: (token: string) =>
    requisitar<{ provedores: ProvedorProvisionamento[] }>('/interno/provedores', {
      headers: { Authorization: `Bearer ${token}` },
    }),
  criarProvedorInterno: (token: string, dados: EntradaProvisionamento) =>
    requisitar<{ provedor: ProvedorProvisionamento }>('/interno/provedores', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(dados),
    }),
  salvarProvedor: (dados: {
    nome: string;
    fuso: string;
    janelaInicio: string;
    janelaFim: string;
    branding: Branding;
  }) => enviar<{ ok: boolean }>('/configuracoes/provedor', 'PUT', dados),
  criarCanal: (dados: {
    nome: string;
    xmaxQueueId: number;
    apiKey: string;
    numeroOrigem: string;
    vazaoPorMinuto: number;
    concorrencia: number;
  }) => enviar<{ canal: Canal }>('/configuracoes/canais', 'POST', dados),
  atualizarCanal: (
    id: string,
    dados: Partial<{
      nome: string;
      xmaxQueueId: number;
      apiKey: string;
      numeroOrigem: string;
      vazaoPorMinuto: number;
      concorrencia: number;
      ativo: boolean;
    }>,
  ) => enviar<{ ok: boolean }>(`/configuracoes/canais/${id}`, 'PUT', dados),
  testarCanal: (id: string) =>
    enviar<{ sucesso: boolean; modo: string; detalhe: string }>(
      `/configuracoes/canais/${id}/testar`,
      'POST',
    ),
  salvarElo: (dados: { baseUrl: string; token?: string; modo: 'real' | 'demo' }) =>
    enviar<{ ok: boolean }>('/configuracoes/elo', 'PUT', dados),
  testarElo: () =>
    enviar<{ sucesso: boolean; modo: string; detalhe: string }>('/configuracoes/elo/testar', 'POST'),
};

export function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ErroApi) return erro.message;
  if (erro instanceof Error) return erro.message;
  return 'Falha inesperada. Tente novamente.';
}

export function camposDoErro(erro: unknown): Record<string, string> {
  return erro instanceof ErroApi && erro.campos ? erro.campos : {};
}
